import { useEffect, useState, type RefObject } from 'react'
import { useStyles } from './ScrollHint.styles'

interface ScrollHintProps {
  /** The scrollable element to watch and jump to the end of. */
  scrollRef: RefObject<HTMLElement | null>
  /**
   * Re-check scrollability when this changes (e.g. list length), since the
   * container's own box may not resize when its content grows.
   */
  contentKey?: unknown
}

const BOTTOM_THRESHOLD = 8

/**
 * Scroll cue for a scrollable list: a down-chevron with a fade, shown only
 * while there is more content below, which jumps to the end of the list. Must
 * be placed inside a `position: relative` wrapper that also holds the scroll
 * container.
 */
export const ScrollHint = ({ scrollRef, contentKey }: ScrollHintProps) => {
  const { classes } = useStyles()
  const [canScrollMore, setCanScrollMore] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      setCanScrollMore(
        el.scrollHeight - el.scrollTop - el.clientHeight > BOTTOM_THRESHOLD
      )
    }
    update()
    el.addEventListener('scroll', update)
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [scrollRef, contentKey])

  if (!canScrollMore) return null

  return (
    <>
      <span className={classes.fade} />
      <button
        type="button"
        className={classes.button}
        aria-label="Scroll to the end of the list"
        onClick={() => {
          const el = scrollRef.current
          if (!el) return
          el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </>
  )
}
